import React, { useState, useMemo, useEffect } from "react";
import { FaTimes, FaTrash, FaEye, FaDownload, FaSave, FaWhatsapp, FaEnvelope, FaArrowLeft } from "react-icons/fa";
import "../assets/css/QuoteGenerator.css";
import { generateQuotePdf } from "../utils/quotePdf";
import {
  loadAvailableQuoteServices,
  saveCustomQuoteServicesToStorage
} from "../utils/quoteServicesCatalog";
import { useConfirm } from "./ConfirmModal";
import axiosInstance from "./api/axiosInstance";

const generateQuoteNumber = () => {
  const now = new Date();
  const dateStr = now.toISOString().split("T")[0].replace(/-/g, "");
  const seq = String(Math.floor(Math.random() * 999) + 1).padStart(3, "0");
  return `TT-Q-${dateStr}-${seq}`;
};

export default function QuoteGenerator({ isOpen, onClose, customerData, type = "client", standalone = false, hideBackInHeader = false, clientId = null }) {
  const confirm = useConfirm();
  const buildAddress = (d) => {
    if (!d) return "";
    const parts = [
      d.addressLine1 || d.address_line_1,
      d.addressLine2 || d.address_line_2,
      d.city || d.City,
      d.state || d.State,
      d.country || d.Country,
      d.zipCode || d.zip_code
    ].filter(Boolean);
    return parts.join(", ");
  };

  const [customerInfo, setCustomerInfo] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    address: "",
    typeOfServices: ""
  });

  const [quoteMeta, setQuoteMeta] = useState({
    quoteNumber: generateQuoteNumber(),
    quoteDate: new Date().toISOString().split("T")[0],
    paymentTerms: "Net 30"
  });

  const [notes, setNotes] = useState("1. This quotation is valid for the period mentioned above.\n2. 50% advance payment required to initiate the project.\n3. GST will be charged as applicable.\n4. Delivery timeline will be shared upon confirmation.");
  const [terms, setTerms] = useState(
    "Important Notes:\n• GST is included for all budget\n• Minimum Ads budget Duration: 7 Days to 15 Days\n\nPayment Pattern:\n• First Term: Advance Payment 80% of monthly service charge\n• Second Term: After completing 20 Days - 20% of previous month payment and 80% of next month advance"
  );
  const [packageTotal, setPackageTotal] = useState("");
  const [packagePeriod, setPackagePeriod] = useState(""); // "" (Select) | "perMonth" | "perYear"

  const [lineItems, setLineItems] = useState([]);
  const [serviceFreeText, setServiceFreeText] = useState({});

  const [availableServices, setAvailableServices] = useState(() => loadAvailableQuoteServices());
  const [loadingServices, setLoadingServices] = useState(false);

  useEffect(() => {
    saveCustomQuoteServicesToStorage(availableServices);
  }, [availableServices]);
  const [editingService, setEditingService] = useState(null);
  const [isAddingServiceToCategory, setIsAddingServiceToCategory] = useState(null);

  const [quoteAmount, setQuoteAmount] = useState(0);
  const [gstRate, setGstRate] = useState(18);
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [additionalChargesLabel, setAdditionalChargesLabel] = useState("Setup Fee");

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [checkmarkImageDataUrl, setCheckmarkImageDataUrl] = useState(null);

  const [leadGenerationCampaign, setLeadGenerationCampaign] = useState(false);
  const [leadGenerationTargets, setLeadGenerationTargets] = useState({
    location: "",
    age: "",
    gender: "Both"
  });
  const [leadGenerationGoals, setLeadGenerationGoals] = useState({
    metaAdsBudget: "",
    reach: "",
    expectedLeads: "",
    googleAdsBudget: ""
  });

  useEffect(() => {
    if (!isOpen || !customerData) return;
    setCustomerInfo({
      companyName: customerData.companyName || customerData.Company_Name || "",
      contactPerson: `${customerData.firstName || customerData.First_Name || ""} ${customerData.lastName || customerData.Last_Name || ""}`.trim(),
      email: customerData.companyEmail || customerData.Company_Email || customerData.personalEmail || customerData.Email || "",
      phone: customerData.companyPhone || customerData.Company_Phone || customerData.personalPhone || customerData.Phone || "",
      address: customerData.address || buildAddress(customerData),
      typeOfServices: customerData.typeOfServices || customerData.type_of_services || ""
    });
  }, [isOpen, customerData]);

  // Reopen PDF preview after browser refresh (payload saved when preview was open)
  useEffect(() => {
    if (!standalone) return;
    try {
      const saved = sessionStorage.getItem("quotePreviewPayload");
      if (!saved) return;
      const payload = JSON.parse(saved);
      const doc = generateQuotePdf(payload);
      const pdfBlob = doc.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      setPreviewUrl(url);
      setShowPreview(true);
      sessionStorage.removeItem("quotePreviewPayload");
    } catch (_) {}
  }, [standalone]);

  // Render FaCheck (react-icons/fa) shape to a PNG data URL for use in PDF
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return;
    const size = 32;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="${size}" height="${size}"><path fill="#22c55e" d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"/></svg>`;
    const svgDataUrl = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svg)));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0, size, size);
          setCheckmarkImageDataUrl(canvas.toDataURL("image/png"));
        }
      } catch (e) {
        console.warn("Checkmark image for PDF could not be created", e);
      }
    };
    img.onerror = () => {};
    img.src = svgDataUrl;
  }, [isOpen]);

  useEffect(() => {
    const num = Number(quoteAmount);
    const rate = Number(gstRate) || 0;
    const gstAmt = num * (rate / 100);
    const totalWithGst = num + gstAmt;
    const exactAmount = Math.floor(totalWithGst);
    if (!isNaN(num) && exactAmount > 0) {
      const amountStr = `₹${exactAmount.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      const suffix = packagePeriod === "perYear" ? "/- Per year" : packagePeriod === "perMonth" ? "/- Per month" : "";
      setPackageTotal(amountStr + suffix);
    } else {
      setPackageTotal("");
    }
  }, [quoteAmount, gstRate, packagePeriod]);

  const servicesByCategory = useMemo(() => {
    return availableServices.reduce((acc, service) => {
      const cat = service.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(service);
      return acc;
    }, {});
  }, [availableServices]);

  const calculations = useMemo(() => {
    const gstAmt = quoteAmount * (gstRate / 100);
    const grandTotal = quoteAmount + gstAmt + Number(additionalCharges || 0);
    return { quoteAmount, gstAmt, grandTotal };
  }, [quoteAmount, gstRate, additionalCharges]);

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleMetaChange = (e) => {
    const { name, value } = e.target;
    setQuoteMeta((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service, isChecked) => {
    if (isChecked) {
      setLineItems((prev) => [
        ...prev,
        {
          id: Date.now() + Math.random(),
          serviceId: service.id,
          customName: service.name,
          description: service.description || "✓",
          quantity: 1,
          unitPrice: service.basePrice,
          discountPercent: 0,
          freeText: serviceFreeText[service.id] || ""
        }
      ]);
    } else {
      setLineItems((prev) => prev.filter((item) => String(item.serviceId) !== String(service.id)));
    }
  };

  const handleServiceFreeTextChange = (serviceId, value) => {
    setServiceFreeText((prev) => ({ ...prev, [serviceId]: value }));
    setLineItems((prev) =>
      prev.map((item) =>
        String(item.serviceId) === String(serviceId) ? { ...item, freeText: value } : item
      )
    );
  };

  const handleAddServiceToCategory = (category) => {
    setEditingService({
      id: `custom-cat-${Date.now()}`,
      name: "",
      category: category,
      basePrice: 0,
      unit: "project",
      description: ""
    });
    setIsAddingServiceToCategory(category);
  };

  const handleDeleteService = async (serviceId) => {
    if (await confirm({ title: "Delete Service", message: "Are you sure you want to delete this service?" })) {
      setAvailableServices((prev) => prev.filter((s) => String(s.id) !== String(serviceId)));
      setLineItems((prev) => prev.filter((item) => String(item.serviceId) !== String(serviceId)));
    }
  };

  const handleSaveService = () => {
    if (!editingService || !editingService.name.trim()) return;
    if (isAddingServiceToCategory) {
      setAvailableServices((prev) => [...prev, editingService]);
    } else {
      setAvailableServices((prev) => prev.map((s) => (String(s.id) === String(editingService.id) ? editingService : s)));
    }
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const handleCancelEditService = () => {
    setEditingService(null);
    setIsAddingServiceToCategory(null);
  };

  const formatCurrency = (amount) => `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;

  const getPdfData = () => ({
    customerInfo,
    quoteMeta,
    lineItems,
    availableServices,
    packageTotal,
    notes,
    terms,
    leadGenerationCampaign,
    leadGenerationTargets,
    leadGenerationGoals,
    checkmarkImageDataUrl
  });

  const handleClosePreview = () => {
    setShowPreview(false);
    try {
      sessionStorage.removeItem("quotePreviewPayload");
    } catch (_) {}
  };

  const handlePreview = () => {
    const payload = getPdfData();
    const doc = generateQuotePdf(payload);
    const pdfBlob = doc.output("blob");
    const url = URL.createObjectURL(pdfBlob);
    setPreviewUrl(url);
    setShowPreview(true);
    try {
      sessionStorage.setItem("quotePreviewPayload", JSON.stringify(payload));
    } catch (_) {}
  };

  const handleDownload = () => {
    const doc = generateQuotePdf(getPdfData());
    doc.save(`DIGIXLEADS_Quote_${quoteMeta.quoteDate || new Date().toISOString().split("T")[0]}.pdf`);
  };

  const getQuotePdfBlob = () => {
    const doc = generateQuotePdf(getPdfData());
    return doc.output("blob");
  };

  const getQuotePdfFileName = () => `DIGIXLEADS_Quote_${quoteMeta.quoteDate || new Date().toISOString().split("T")[0]}.pdf`;

  /** Tenant quotes: keep the sent PDF on the tenant (attachments) and mark Quote Sent. */
  const recordQuoteSent = async (blob, fileName) => {
    if (!clientId) return;
    try {
      const form = new FormData();
      form.append("file", new File([blob], fileName, { type: "application/pdf" }));
      form.append("remarks", `Quote ${quoteMeta.quoteNumber || ""}`.trim());
      await axiosInstance.post(`/client/${clientId}/quote`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSavedMsg("Quote saved to tenant and marked as sent.");
      setTimeout(() => setSavedMsg(""), 4000);
    } catch (err) {
      console.error("Could not save quote to tenant:", err);
      setSavedMsg("Quote sent, but could not be saved to the tenant.");
      setTimeout(() => setSavedMsg(""), 5000);
    }
  };

  const handleEmail = async () => {
    if (!customerInfo.email) {
      alert("Please enter a customer email first.");
      return;
    }
    const subject = `DIGIXLEADS Proposal - ${customerInfo.companyName || "Quote"}`;
    const bodyText = `Hello,\n\nPlease find the quotation details in the attached PDF.\n\nDate: ${quoteMeta.quoteDate || new Date().toISOString().split("T")[0]}`;
    const fileName = getQuotePdfFileName();
    const blob = getQuotePdfBlob();
    const file = new File([blob], fileName, { type: "application/pdf" });

    if (typeof navigator !== "undefined" && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: subject,
          text: bodyText,
          files: [file]
        });
        setSavedMsg("Shared successfully!");
        setTimeout(() => setSavedMsg(""), 3000);
        recordQuoteSent(blob, fileName);
      } catch (err) {
        if (err.name !== "AbortError") {
          fallbackEmailShare(customerInfo.email, subject, bodyText, blob, fileName);
        }
      }
    } else {
      fallbackEmailShare(customerInfo.email, subject, bodyText, blob, fileName);
    }
  };

  const fallbackEmailShare = (email, subject, bodyText, pdfBlob, fileName) => {
    recordQuoteSent(pdfBlob, fileName);
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    const mailtoBody = `${bodyText}\n\n(The quote PDF has been downloaded — please attach it to this email.)`;
    const companyEmail = "info@tumma.co";
    window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailtoBody)}&cc=${encodeURIComponent(companyEmail)}`;
  };

  const handleWhatsApp = async () => {
    if (!customerInfo.phone) {
      alert("Please enter a customer phone number first.");
      return;
    }
    const cleanPhone = customerInfo.phone.replace(/[^0-9]/g, "");
    const finalPhone = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone;
    const message = `Please find the quotation PDF attached. Date: ${quoteMeta.quoteDate || new Date().toISOString().split("T")[0]}`;
    const fileName = getQuotePdfFileName();
    const blob = getQuotePdfBlob();
    const file = new File([blob], fileName, { type: "application/pdf" });

    if (typeof navigator !== "undefined" && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: "Quote",
          text: message,
          files: [file]
        });
        setSavedMsg("Shared successfully!");
        setTimeout(() => setSavedMsg(""), 3000);
        recordQuoteSent(blob, fileName);
      } catch (err) {
        if (err.name !== "AbortError") {
          fallbackWhatsAppShare(finalPhone, message, blob, fileName);
        }
      }
    } else {
      fallbackWhatsAppShare(finalPhone, message, blob, fileName);
    }
  };

  const fallbackWhatsAppShare = (phone, message, pdfBlob, fileName) => {
    recordQuoteSent(pdfBlob, fileName);
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message + "\n\n(Please attach the downloaded PDF when sending.)")}`, "_blank");
  };

  const saveDraft = () => {
    const draft = {
      customerInfo,
      quoteMeta,
      lineItems,
      notes,
      terms,
      packageTotal,
      quoteAmount,
      gstRate,
      additionalCharges,
      additionalChargesLabel,
      leadGenerationCampaign,
      leadGenerationTargets,
      leadGenerationGoals,
      savedAt: new Date().toISOString()
    };
    const drafts = JSON.parse(localStorage.getItem("quoteDrafts") || "[]");
    drafts.push(draft);
    localStorage.setItem("quoteDrafts", JSON.stringify(drafts));
    setSavedMsg("Draft saved!");
    setTimeout(() => setSavedMsg(""), 3000);
  };

  if (!standalone && !isOpen) return null;

  const headerActions = (
    <div className="quote-generator-header-actions" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {savedMsg && <span className="quote-saved-msg">{savedMsg}</span>}
      {standalone && !hideBackInHeader && (
        <button type="button" className="btn-quote btn-quote-back" onClick={onClose}><FaArrowLeft /> Back</button>
      )}
      <button className="btn-quote btn-quote-whatsapp" onClick={handleWhatsApp}><FaWhatsapp /> WhatsApp</button>
      <button className="btn-quote btn-quote-email" onClick={handleEmail}><FaEnvelope /> Email</button>
      <button className="btn-quote-preview" onClick={handlePreview}><FaEye /> Preview</button>
      <button className="btn-quote-download" onClick={handleDownload}><FaDownload /> PDF</button>
      {!standalone && <button className="quote-generator-close" onClick={onClose}><FaTimes /></button>}
    </div>
  );

  const content = (
    <>
      {!standalone && (
        <div className="quote-generator-header">
          <h2>Generate Quote</h2>
          <div className="quote-generator-header-actions">
            {savedMsg && <span className="quote-saved-msg">{savedMsg}</span>}
            <button className="btn-quote btn-quote-whatsapp" onClick={handleWhatsApp}><FaWhatsapp /> WhatsApp</button>
            <button className="btn-quote btn-quote-email" onClick={handleEmail}><FaEnvelope /> Email</button>
            <button className="btn-quote-preview" onClick={handlePreview}><FaEye /> Preview</button>
            <button className="btn-quote-download" onClick={handleDownload}><FaDownload /> PDF</button>
            <button className="quote-generator-close" onClick={onClose}><FaTimes /></button>
          </div>
        </div>
      )}

      {standalone && !hideBackInHeader && (
        <div className="quote-generator-header quote-generator-header-standalone">
          <h2>Generate Quote</h2>
          {headerActions}
        </div>
      )}

      <div className="quote-generator-content">
          {standalone && (
            <button type="button" className="quote-back-link" onClick={onClose}>
              <FaArrowLeft /> Back
            </button>
          )}
            <div className="quote-generator-form">
              {/* Customer Information */}
              <div className="quote-form-section">
                <h3>Customer Information</h3>
                <div className="quote-form-grid">
                  <div className="quote-form-group">
                    <label>Company Name</label>
                    <input type="text" name="companyName" value={customerInfo.companyName} onChange={handleCustomerInfoChange} placeholder="e.g., TechVista Solutions" />
                  </div>
                  <div className="quote-form-group">
                    <label>Tenant Name</label>
                    <input type="text" name="contactPerson" value={customerInfo.contactPerson} onChange={handleCustomerInfoChange} placeholder="e.g., Rahul Sharma" />
                  </div>
                  <div className="quote-form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={customerInfo.email} onChange={handleCustomerInfoChange} placeholder="email@company.com" />
                  </div>
                  <div className="quote-form-group">
                    <label>Phone</label>
                    <input type="tel" name="phone" value={customerInfo.phone} onChange={handleCustomerInfoChange} placeholder="+91 98765 43210" />
                  </div>
                  <div className="quote-form-group">
                    <label>Address</label>
                    <input type="text" name="address" value={customerInfo.address} onChange={handleCustomerInfoChange} placeholder="Full address" />
                  </div>
                  <div className="quote-form-group">
                    <label>Type of Services</label>
                    <input type="text" name="typeOfServices" value={customerInfo.typeOfServices} onChange={handleCustomerInfoChange} placeholder="e.g., Website, Digital Marketing" />
                  </div>
                </div>
              </div>

              {/* Select Packages */}
              <div className="quote-form-section">
                <h3 className="quote-packages-section-title">Select Packages</h3>
                {loadingServices ? (
                  <p className="quote-services-loading">Loading services...</p>
                ) : Object.keys(servicesByCategory).length === 0 ? (
                  <p className="quote-services-empty">No service categories found.</p>
                ) : (
                <div className="quote-packages-grid">
                  {Object.entries(servicesByCategory).map(([category, services]) => (
                    <div key={category} className="quote-service-category quote-tree-category">
                      <h4 className="quote-category-title">{category}</h4>
                      <div className="quote-tree-list">
                        {services.map((service, index) => {
                          const isSelected = lineItems.some((item) => String(item.serviceId) === String(service.id));
                          const isMainPackage = service.basePrice > 0;
                          return (
                            <div key={service.id} className="quote-tree-item">
                              <div className="quote-tree-item-left">
                                <span className={isMainPackage ? "quote-tree-name-main" : "quote-tree-name"}>{service.name}</span>
                              </div>
                              <input
                                type="text"
                                className="quote-service-freetext"
                                value={serviceFreeText[service.id] || ""}
                                onChange={(e) => handleServiceFreeTextChange(service.id, e.target.value)}
                                placeholder="Add notes..."
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="quote-tree-item-right">
                                <label className="quote-service-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleServiceToggle(service, e.target.checked)}
                                  />
                                </label>
                                <button type="button" className="btn-icon btn-icon-delete" onClick={() => handleDeleteService(service.id)} title="Delete">
                                  <FaTrash size={12} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <div className="quote-tree-add">
                          <button type="button" className="btn-add-service" onClick={() => handleAddServiceToCategory(category)}>
                            Add Service
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>

              {/* Amount Calculator & Lead Generation Campaign - side by side */}
              <div className="quote-form-section quote-calculator-lead-section">
                <h3 className="quote-packages-section-title">Amount Calculator</h3>
                <div className="quote-calculator-lead-row">
                  <div className="quote-totals-box">
                    <div className="quote-totals-row">
                      <span className="quote-totals-label">Quote Amount (₹)</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, justifyContent: "flex-end" }}>
                        <input
                          type="number"
                          min={0}
                          value={quoteAmount}
                          onChange={(e) => setQuoteAmount(parseFloat(e.target.value) || 0)}
                          className="quote-totals-input"
                          style={{ width: "100px" }}
                        />
                        <select
                          value={packagePeriod}
                          onChange={(e) => setPackagePeriod(e.target.value)}
                          className="quote-period-select"
                          style={{ minWidth: "120px", padding: "6px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
                        >
                          <option value="">Select</option>
                          <option value="perMonth">/- Per month</option>
                          <option value="perYear">/- Per year</option>
                        </select>
                      </div>
                    </div>
                    <div className="quote-totals-row">
                      <span className="quote-totals-label">GST <input type="number" min={0} max={28} value={gstRate} onChange={(e) => setGstRate(parseFloat(e.target.value) || 0)} className="quote-gst-input" /> %</span>
                      <span>{formatCurrency(calculations.gstAmt)}</span>
                    </div>
                    <div className="quote-totals-row quote-grand-total">
                      <span>Package Total Amount</span>
                      <span>{packageTotal || "—"}</span>
                    </div>
                  </div>
                  <div className="quote-lead-generation-box">
                    <label className="quote-lead-generation-checkbox">
                      <input
                        type="checkbox"
                        checked={leadGenerationCampaign}
                        onChange={(e) => setLeadGenerationCampaign(e.target.checked)}
                      />
                      <span className="quote-lead-generation-label">Lead Generation Campaign</span>
                    </label>
                    {leadGenerationCampaign && (
                  <div className="quote-lead-generation-form">
                    <div className="quote-lead-generation-targets">
                      <h4>Targets</h4>
                      <div className="quote-form-grid">
                        <div className="quote-form-group">
                          <label>Location</label>
                          <input
                            type="text"
                            value={leadGenerationTargets.location}
                            onChange={(e) => setLeadGenerationTargets((prev) => ({ ...prev, location: e.target.value }))}
                            placeholder="e.g., Hyderabad"
                          />
                        </div>
                        <div className="quote-form-group">
                          <label>Age</label>
                          <input
                            type="text"
                            value={leadGenerationTargets.age}
                            onChange={(e) => setLeadGenerationTargets((prev) => ({ ...prev, age: e.target.value }))}
                            placeholder="e.g., 30 to 60"
                          />
                        </div>
                        <div className="quote-form-group">
                          <label>Gender</label>
                          <select
                            value={leadGenerationTargets.gender}
                            onChange={(e) => setLeadGenerationTargets((prev) => ({ ...prev, gender: e.target.value }))}
                          >
                            <option value="Both">Both</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="quote-lead-generation-goals">
                      <h4>Goals (Per day)</h4>
                      <div className="quote-form-grid">
                        <div className="quote-form-group">
                          <label>Ad Budget (Meta Ads) (₹)</label>
                          <input
                            type="number"
                            min={0}
                            value={leadGenerationGoals.metaAdsBudget}
                            onChange={(e) => setLeadGenerationGoals((prev) => ({ ...prev, metaAdsBudget: e.target.value }))}
                            placeholder="e.g., 500"
                          />
                        </div>
                        <div className="quote-form-group">
                          <label>Reach</label>
                          <input
                            type="text"
                            value={leadGenerationGoals.reach}
                            onChange={(e) => setLeadGenerationGoals((prev) => ({ ...prev, reach: e.target.value }))}
                            placeholder="e.g., Min 15,000 to 35,000"
                          />
                        </div>
                        <div className="quote-form-group">
                          <label>Expected Leads</label>
                          <input
                            type="text"
                            value={leadGenerationGoals.expectedLeads}
                            onChange={(e) => setLeadGenerationGoals((prev) => ({ ...prev, expectedLeads: e.target.value }))}
                            placeholder="e.g., 5 to 10 Leads"
                          />
                        </div>
                        <div className="quote-form-group">
                          <label>Ad Budget (Google Ads) (₹)</label>
                          <input
                            type="number"
                            min={0}
                            value={leadGenerationGoals.googleAdsBudget}
                            onChange={(e) => setLeadGenerationGoals((prev) => ({ ...prev, googleAdsBudget: e.target.value }))}
                            placeholder="e.g., 500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="quote-form-section">
                <h3>Terms & Conditions</h3>
                <div className="quote-form-group full-width">
                  <textarea className="quote-terms-textarea" value={terms} onChange={(e) => setTerms(e.target.value)} rows={14} placeholder="Payment terms, delivery terms, etc." />
                </div>
              </div>

              {/* Action buttons at bottom of form card */}
              {standalone && (
                <div className="quote-form-actions">
                  {savedMsg && <span className="quote-saved-msg">{savedMsg}</span>}
                  <button className="btn-quote btn-quote-whatsapp" onClick={handleWhatsApp}><FaWhatsapp /> WhatsApp</button>
                  <button className="btn-quote btn-quote-email" onClick={handleEmail}><FaEnvelope /> Email</button>
                  <button className="btn-quote-preview" onClick={handlePreview}><FaEye /> Preview</button>
                  <button className="btn-quote-download" onClick={handleDownload}><FaDownload /> PDF</button>
                </div>
              )}
            </div>

          {showPreview && previewUrl ? (
            <div className="quote-preview-overlay quote-pdf-preview-overlay" onClick={handleClosePreview}>
              <div className="quote-preview-popup" onClick={(e) => e.stopPropagation()}>
                <div className="quote-preview-header">
                  <h3>Quote PDF Preview</h3>
                  <button type="button" onClick={handleClosePreview}>Close</button>
                </div>
                <embed src={`${previewUrl}#toolbar=0&navpanes=0`} type="application/pdf" className="quote-preview-iframe" title="Quote Preview" />
              </div>
            </div>
          ) : null}
        </div>
      </>
  );

  const editModal = editingService && (
    <div className="quote-preview-overlay" style={{ zIndex: 1001 }} onClick={handleCancelEditService}>
      <div className="quote-edit-service-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{isAddingServiceToCategory ? `Add Package to ${isAddingServiceToCategory}` : "Edit Package"}</h3>
        <div className="quote-form-group">
          <label>Package Name</label>
          <input
            type="text"
            value={editingService.name}
            onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
            placeholder="e.g. Meta Ads Setup"
          />
        </div>
        <div className="quote-edit-service-actions">
          <button type="button" className="btn-quote-secondary" onClick={handleCancelEditService}>Cancel</button>
          <button type="button" className="btn-quote-preview" onClick={handleSaveService}>Save</button>
        </div>
      </div>
    </div>
  );

  return standalone ? (
    <div className="quote-generator-page">
      {content}
      {editModal}
    </div>
  ) : (
    <div className="quote-generator-overlay" onClick={onClose}>
      <div className="quote-generator-modal quote-generator-modal-integrated" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
      {editModal}
    </div>
  );
}
